import { useCallback, useEffect, useState } from "react";
import { api } from "@/lib/apiClient";
import { formatArrivalDayLong } from "@/lib/bookingDates";
import { guestTypeLabel } from "@/lib/guestType";
import { formatCurrency } from "@/lib/money";
import type { GuestFrequencyReport } from "@/types/camping";
import { LogicNote, StatCard, downloadCsv } from "@/pages/reports/reportShared";

export function GuestFrequencyReportSection() {
  const [report, setReport] = useState<GuestFrequencyReport | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    api
      .get<GuestFrequencyReport>("/api/v1/reports/guest-frequency?limit=100&min_stays=1")
      .then(setReport)
      .catch((err: unknown) => {
        setReport(null);
        setError(
          err instanceof Error ? err.message : "Gäste-Auswertung konnte nicht geladen werden.",
        );
      })
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const currency = report?.currency ?? "EUR";

  const exportCsv = () => {
    if (!report) return;
    const rows: string[][] = [
      [
        "Gast",
        "E-Mail",
        "Gästetyp",
        "Aufenthalte",
        "Nächte gesamt",
        "Erste Anreise",
        "Letzte Abreise",
        "Buchungsumsatz",
        "Rechnungsumsatz",
      ],
      ...report.items.map((row) => [
        row.guest_name,
        row.email ?? "",
        guestTypeLabel(row.guest_type),
        String(row.stay_count),
        String(row.total_nights),
        row.first_arrival ?? "",
        row.last_departure ?? "",
        row.booking_revenue_total,
        row.invoiced_total,
      ]),
    ];
    downloadCsv("gaeste_auswertung.csv", rows);
  };

  const leaders = report?.items.slice(0, 8) ?? [];
  const maxStays = Math.max(...leaders.map((row) => row.stay_count), 1);

  return (
    <section className="space-y-6">
      <header className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
        <div>
          <h2 className="text-xl font-semibold text-label">Stammgäste & Aufenthalte</h2>
          <p className="mt-1 text-sm text-label-secondary">
            Welche Gäste am häufigsten bei uns waren – sortiert nach Anzahl der Aufenthalte
          </p>
        </div>
        <button
          type="button"
          className="eh-btn eh-btn--secondary min-h-[44px] whitespace-nowrap"
          disabled={!report}
          onClick={exportCsv}
        >
          CSV exportieren
        </button>
      </header>

      {error ? (
        <p className="rounded-lg border border-status-red/30 bg-status-red/10 px-4 py-3 text-sm text-status-red">
          {error}
        </p>
      ) : null}

      {loading ? (
        <p className="text-sm text-label-secondary">Gäste-Auswertung wird geladen…</p>
      ) : report ? (
        <>
          <div className="grid gap-4 sm:grid-cols-2">
            <StatCard
              label="Gäste mit Aufenthalten"
              value={report.total_guests_with_stays.toLocaleString("de-DE")}
              hint="Mindestens ein bestätigter, laufender oder abgeschlossener Aufenthalt."
            />
            <StatCard
              label="In dieser Liste"
              value={report.items.length.toLocaleString("de-DE")}
              hint="Die häufigsten Gäste, sortiert nach Anzahl der Aufenthalte."
            />
          </div>

          {leaders.length > 0 ? (
            <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
              <h3 className="font-medium text-label">Häufigste Gäste</h3>
              <p className="mt-1 text-sm text-label-secondary">
                Balken nach Anzahl der Aufenthalte. Die Tabelle darunter enthält Nächte und Umsatz.
              </p>
              <div className="mt-4 space-y-3">
                {leaders.map((row, index) => (
                  <div key={row.guest_id}>
                    <div className="mb-1 flex items-baseline justify-between gap-3 text-sm">
                      <span className="min-w-0 truncate">
                        <span className="mr-2 tabular-nums text-label-tertiary">{index + 1}</span>
                        <span className="font-medium text-label">{row.guest_name}</span>
                      </span>
                      <span className="shrink-0 tabular-nums text-label-secondary">
                        {row.stay_count.toLocaleString("de-DE")} Aufenthalte ·{" "}
                        {row.total_nights.toLocaleString("de-DE")} Nächte
                      </span>
                    </div>
                    <div className="h-2 overflow-hidden rounded-full bg-fill-tertiary">
                      <div
                        className="h-full rounded-full bg-accent"
                        style={{ width: `${Math.max(4, (row.stay_count / maxStays) * 100)}%` }}
                      />
                    </div>
                  </div>
                ))}
              </div>
            </section>
          ) : null}

          <section className="rounded-xl border border-separator bg-surface p-4 shadow-sm">
            <h3 className="font-medium text-label">Gäste nach Häufigkeit</h3>
            <p className="mt-1 text-sm text-label-secondary">
              Aufenthalte, Übernachtungen und Umsätze je Gast. Details und Historie finden Sie unter
              Gäste → Gast öffnen.
            </p>
            {report.items.length === 0 ? (
              <p className="mt-4 text-sm text-label-secondary">Noch keine Aufenthalte erfasst.</p>
            ) : (
              <div className="mt-4 overflow-x-auto">
                <table className="eh-table w-full min-w-[48rem] text-sm">
                  <thead>
                    <tr>
                      <th className="text-left">#</th>
                      <th className="text-left">Gast</th>
                      <th className="text-left">Typ</th>
                      <th className="text-right">Aufenthalte</th>
                      <th className="text-right">Nächte</th>
                      <th className="text-left">Erste Anreise</th>
                      <th className="text-left">Letzte Abreise</th>
                      <th className="text-right">Buchungsumsatz</th>
                      <th className="text-right">Rechnungsumsatz</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.items.map((row, index) => (
                      <tr key={row.guest_id}>
                        <td className="tabular-nums text-label-secondary">{index + 1}</td>
                        <td>
                          <span className="font-medium text-label">{row.guest_name}</span>
                          {row.email ? (
                            <span className="mt-0.5 block text-xs text-label-secondary">
                              {row.email}
                            </span>
                          ) : null}
                        </td>
                        <td className="text-label-secondary">{guestTypeLabel(row.guest_type)}</td>
                        <td className="text-right tabular-nums font-medium">{row.stay_count}</td>
                        <td className="text-right tabular-nums">{row.total_nights}</td>
                        <td className="text-label-secondary">
                          {row.first_arrival ? formatArrivalDayLong(row.first_arrival) : "—"}
                        </td>
                        <td className="text-label-secondary">
                          {row.last_departure ? formatArrivalDayLong(row.last_departure) : "—"}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatCurrency(row.booking_revenue_total, row.currency || currency)}
                        </td>
                        <td className="text-right tabular-nums">
                          {formatCurrency(row.invoiced_total, row.currency || currency)}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </section>

          <LogicNote>
            <p>{report.logic_note}</p>
          </LogicNote>
        </>
      ) : null}
    </section>
  );
}
